import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  tap,
  throwError,
} from 'rxjs';
import { HttpService } from './http.service';
import { environment } from '../../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { OverlayListenerOptions, OverlayOptions } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  baseUrl = '';
  env = environment;
  defaultImageUrl: string = `${this.env.assetUrl}/user_placeholder.png`;
  httpService = inject(HttpService);
  public memberList$ = new BehaviorSubject([]);
  public themeList$ = new BehaviorSubject([]);
  public wpList$ = new BehaviorSubject([]);
  public brandList$ = new BehaviorSubject([]);
  public quarterlyOutcomesList$ = new BehaviorSubject([]);
  public myTaks$ = new BehaviorSubject([]);
  public memeberListsByWP$ = new BehaviorSubject([]);
  public fetchWorkpackageByMemberId$ = new BehaviorSubject([]);
  public overAllGroup$ = new BehaviorSubject([]);
  public parentStandaloneGroupList$ = new BehaviorSubject([]);
  public outcomeDataList$ = new BehaviorSubject([]);
  public backlogDatas$ = new BehaviorSubject([]);
  public isOutcomeDataLoad$ = new BehaviorSubject(false);

  public isLoading$ = new BehaviorSubject(false);

  loggedInUser: any = {};

  generateYearArray = (startYear: number): { key: number; value: number }[] => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
      const year = startYear + i;
      return { key: year, value: year };
    });
  };

  getFilledPropertiesCount(data: any) {
    return Object.keys(data).filter(
      (key) => data[key] !== null && data[key] !== '' && data[key] != undefined
    )?.length;
  }

  constructor(private cookieService: CookieService) {
    if (this.cookieService.get('auth')) {
      this.loggedInUser = JSON.parse(
        window.atob(this.cookieService.get('auth'))
      ).data;
    }
  }

  fetchOverAllGroup() {
    this.isLoading$.next(true);
    this.httpService
      .post(`${environment.opbBackendHost}/api/member/all/groups`, {})
      .subscribe((res: any) => {
        this.overAllGroup$.next(res?.data);
        this.isLoading$.next(false);
      });
  }

  // fetchParentStandaloneGroupList(cat: any, page: any = '') {
  //   let data = {};
  //   if (page != '') {
  //     data = { member_id: this.loggedInUser.user_id };
  //   }
  //   this.isLoading$.next(true);
  //   this.httpService
  //     .post(`${environment.opbBackendHost}/api/${cat}/parent/groups`, data)
  //     .subscribe((res: any) => {
  //       this.parentStandaloneGroupList$.next(res?.data);
  //       this.isLoading$.next(false);
  //     });
  // }

  fetchParentStandaloneGroupList(cat: any, page: any = ''): Observable<any> {
    let data = {};
    if (page !== '') {
      data = { member_id: this.loggedInUser.user_id };
    }
    return this.httpService
      .post(`${environment.opbBackendHost}/api/${cat}/parent/groups`, data)
      .pipe(
        tap((res) => this.parentStandaloneGroupList$.next(res?.data)),
        catchError((error) => {
          console.error('Error fetching groups:', error);
          return of(null);
        })
      );
  }

  fetchWPList(data?: any) {
    this.isLoading$.next(true);
    this.httpService
      .post(`${environment.opbBackendHost}/api/member/workpackage/list`, {
        page: 1,
        ...data,
      })
      .subscribe((res: any) => {
        this.wpList$.next(res?.data);
        this.isLoading$.next(false);
      });
  }

  fetchBrandList() {
    this.isLoading$.next(true);
    this.httpService
      .post(`${environment.opbBackendHost}/api/member/workpackage/brands`, {})
      .subscribe((res: any) => {
        this.brandList$.next(res?.data);
        this.isLoading$.next(false);
      });
  }

  fetchMembersList(role = 'member', payload?: any): Observable<any> {
    return this.httpService
      .post(`${environment.opbBackendHost}/api/${role}/group/members`, {
        ...payload,
      })
      .pipe(
        tap((res: any) => {
          this.memberList$.next(res?.data);
        }),
        catchError((error) => {
          console.error('Error fetching members:', error);
          return throwError(error);
        })
      );
  }

  fetchThemeList(year?: any) {
    this.httpService
      .post(`${environment.opbBackendHost}/api/member/themes`, { year })
      .subscribe((res: any) => {
        this.themeList$.next(res?.data);
      });
  }

  fetchQuarterlyOutcomesLists(list: any) {
    let data = {
      package_id: list?.package_id,
    };
    this.httpService
      .post(
        `${environment.opbBackendHost}/api/member/workpackage/quarterly/outcomes`,
        data
      )
      .subscribe((res: any) => {
        this.quarterlyOutcomesList$.next(res?.data);
      });
  }

  fetchTasksLists(task?: any) {
    let data = {
      search: task?.search || '',
      page: 1,
      ...task,
    };
    this.httpService
      .post(`${environment.opbBackendHost}/api/member/my/tasks`, data)
      .subscribe((res: any) => {
        this.myTaks$.next(res?.data);
      });
  }

  fetchMemeberListsByWP(pkgId: any) {
    let data = {
      package_id: pkgId,
    };
    this.httpService
      .post(
        `${environment.opbBackendHost}/api/member/workpackage/ongoing/tasks`,
        data
      )
      .subscribe((res: any) => {
        if (res?.code == 200) {
          this.memeberListsByWP$.next(res?.data);
        }
      });
  }

  getSubgroupListData(
    role: any,
    parentIds: any,
    isHome = false
  ): Observable<any> {
    return this.httpService.post(
      `${environment.opbBackendHost}/api/${role}/child/groups`,
      {
        parents: String(parentIds),
        member_id: isHome ? this.loggedInUser.user_id : '',
      }
    );
  }

  fetchWorkpackageByMemberId(member_id: any) {
    let data = {
      member_id: member_id,
    };
    this.httpService
      .post(`${environment.opbBackendHost}/api/member/workpackage/list`, data)
      .subscribe((res: any) => {
        if (res?.code == 200) {
          this.fetchWorkpackageByMemberId$.next(res?.data);
        }
      });
  }

  fetchThemeListBasedOnYear(year: any): Observable<any> {
    return this.httpService.post(
      `${environment.opbBackendHost}/api/member/themes`,
      { year }
    );
  }

  fetchquarterListBasedOnYear(cat: any, year: any): Observable<any> {
    return this.httpService.post(
      `${environment.opbBackendHost}/api/${cat}/quarters`,
      { year }
    );
  }

  // Remove Primeng dropdown closing effect on scroll
  getOverlayOptions(): OverlayOptions {
    return {
      listener: (event: Event, options?: OverlayListenerOptions) => {
        if (options?.type === 'scroll') {
          return false;
        }
        return options?.valid;
      },
    };
  }

  // Clear the dropdown search filter
  clearDropdownFilter(dropdown: any) {
    if (dropdown && dropdown.filterValue) {
      dropdown.resetFilter();
    }
  }

  fetchCurrentQuater(): Observable<any> {
    return this.httpService
    .post(`${environment.opbBackendHost}/api/member/current/quarter`, {});
  }
    
}
